import graphql from "babel-plugin-relay/macro";
import { useSetAtom } from "jotai";
import { useEffect, useMemo } from "react";
import { useFragment } from "react-relay";

import { selectedRoundAtom } from "../stores";
import { useInitializeRound_Booking$key } from "./__generated__/useInitializeRound_Booking.graphql";

type Props = {
  bookingRef: useInitializeRound_Booking$key | null;
  roundId: string;
  playerId: string;
};

export const useInitializeRound = ({
  bookingRef,
  roundId,
  playerId,
}: Props) => {
  const booking = useFragment(useInitializeRoundFragmentString, bookingRef);

  const setSelectedRound = useSetAtom(selectedRoundAtom);

  const round = useMemo(() => {
    return booking?.rounds?.edges?.find((edge) => edge?.node?.id === roundId);
  }, [booking?.rounds?.edges, roundId]);

  const slotRate = useMemo(() => {
    if (!round?.node?.roundRate?.rate?.id || !booking?.startSlot?.playerRates) {
      return undefined;
    }

    return booking.startSlot.playerRates.find(
      (playerRate) => playerRate?.rate?.id === round.node?.roundRate?.rate?.id
    );
  }, [booking?.startSlot?.playerRates, round?.node?.roundRate?.rate?.id]);

  const isGolfFacility =
    booking?.startSlot?.agenda?.facility?.type === "GOLF_COURSE";

  const permittedCourseLayouts = useMemo(
    () =>
      booking?.startSlot?.permittedCourseLayouts?.edges?.map(
        (edge) => edge?.node
      ) ?? [],
    [booking?.startSlot?.permittedCourseLayouts?.edges]
  );

  useEffect(() => {
    const roundHoleCount = round?.node?.courseLayout?.holeCount;
    const roundCourseLayoutId = round?.node?.courseLayout?.id;
    const hasPermittedCourseLayouts = permittedCourseLayouts.length > 0;
    const hasMatchingPermittedCourseLayout = permittedCourseLayouts.some(
      (layout) => layout?.id === roundCourseLayoutId
    );
    const shouldFallbackCourseLayout =
      isGolfFacility &&
      hasPermittedCourseLayouts &&
      (!roundCourseLayoutId || !hasMatchingPermittedCourseLayout);
    const fallbackCourseLayout = shouldFallbackCourseLayout
      ? permittedCourseLayouts.find(
          (layout) => layout?.holeCount === (roundHoleCount ?? "EIGHTEEN")
        ) ??
        permittedCourseLayouts.find(
          (layout) => layout?.holeCount === "EIGHTEEN"
        ) ??
        permittedCourseLayouts[0]
      : undefined;

    setSelectedRound({
      id: round?.node?.id ?? "",
      courseLayoutId: shouldFallbackCourseLayout
        ? fallbackCourseLayout?.id
        : roundCourseLayoutId,
      courseLayoutHoleCount:
        roundHoleCount ?? fallbackCourseLayout?.holeCount ?? undefined,
      transportationTypeId: round?.node?.transportationType?.id,
      type: isGolfFacility ? "Golf" : "Duration",
      playerId,
      rate: round?.node?.roundRate?.rate?.id
        ? {
            id: round?.node?.roundRate?.rate?.id ?? "",
            name: round?.node?.roundRate?.rate?.name ?? "",
            visibility: round?.node?.roundRate?.rate?.visibility ?? "",
            appliesToMembershipType:
              round?.node?.roundRate?.appliesToMembershipType ?? false,
            nineHolePrice:
              round?.node?.roundRate?.nineHolePrice ?? slotRate?.nineHolePrice,
            eighteenHolePrice:
              round?.node?.roundRate?.eighteenHolePrice ??
              slotRate?.eighteenHolePrice,
            isLocked:
              (round?.node?.roundRate?.rate?.visibility === "QUALIFYING" &&
                !round?.node?.roundRate?.appliesToMembershipType) ||
              round?.node?.roundRate?.rate?.visibility === "NONE",
          }
        : undefined,
    });
  }, [
    booking,
    setSelectedRound,
    isGolfFacility,
    round?.node?.courseLayout?.holeCount,
    round?.node?.courseLayout?.id,
    round?.node?.roundRate?.appliesToMembershipType,
    round?.node?.roundRate?.rate?.id,
    round?.node?.roundRate?.rate?.name,
    round?.node?.roundRate?.rate?.visibility,
    round?.node?.transportationType?.id,
    round?.node?.roundRate?.eighteenHolePrice,
    round?.node?.roundRate?.nineHolePrice,
    playerId,
    slotRate?.nineHolePrice,
    slotRate?.eighteenHolePrice,
    round?.node?.id,
    permittedCourseLayouts,
  ]);
};

const useInitializeRoundFragmentString = graphql`
  fragment useInitializeRound_Booking on Booking {
    startSlot {
      id
      ... on TimeSlot {
        agenda {
          facility {
            type
          }
        }
        permittedCourseLayouts(memberVisible: true) {
          edges {
            node {
              id
              holeCount
            }
          }
        }
      }
      playerRates(type: CUSTOMER) {
        rate {
          id
          name
        }
        nineHolePrice: totalGolfPrice(holeCount: NINE)
        eighteenHolePrice: totalGolfPrice(holeCount: EIGHTEEN)
      }
    }
    rounds {
      edges {
        node {
          id
          __typename
          transportationType {
            id
          }
          courseLayout {
            id
            holeCount
          }
          roundRate {
            appliesToMembershipType
            nineHolePrice: totalGolfPrice(holeCount: NINE)
            eighteenHolePrice: totalGolfPrice(holeCount: EIGHTEEN)
            rate {
              id
              name
              visibility
            }
          }
        }
      }
    }
  }
`;
